import {EditOutlined} from '@ant-design/icons/lib';
import {Alert, Button, Card, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {
  AccountExtraFeeRegion,
  AccountExtraFeeType,
  AccountInvoicePaymentMethod,
  AccountSteps,
  BillingPlans,
  useAccountActualFeesQuery
} from 'api/generated';
import {AnchorLink, Hint, PropertyList, Stack, TableCard} from 'components';
import {CustomAccountInvoiceNotes} from 'components/CustomAccountInvoiceNotes';
import {ScrollToError} from 'components/Form/ScrollToError';
import {Formik} from 'formik';
import {Form, FormItem, SubmitButton} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import * as iban from 'ibantools';
import {formatBoolean, formatIBAN, formatInvoicePaymentMethod, formatPercent} from 'lib/formaters';
import {omitEmpty} from 'lib/omitEmpty';
import {isUndefined} from 'lib/utils';
import React, {useState} from 'react';
import {SectionProps} from 'routes/Account/types';
import * as yup from 'yup';
import {BillingDetailsForm} from './BillingDetailsForm';
import {BillingFormValues} from './types';
import {ExtendedAccountFees} from './types';
import {
  accountInvoicesVatOptions,
  checkIfRegional,
  formatBigNumber,
  formatExtraFee,
  formatFee,
  formatFlatFee
} from './utils';

const validationSchema = yup.object().shape({
  iban: yup
    .string()
    .test('iban', 'invalid IBAN', (value) => !value || iban.isValidIBAN(value!))
    .nullable(),
  fees: yup.object().shape({
    dailyExtraSeatsFee: yup.number().min(0).nullable(),
    dailyPlatformFee: yup.number().min(0).nullable(),
    weeklyCustomDomainFee: yup.number().min(0).nullable(),
    steps: yup
      .array()
      .when('customizeSteps', {
        is: true,
        then: yup.array().of(
          yup.object().shape({
            from: yup.number().min(0).required(),
            trxFlatFee: yup.number().min(0).required(),
            trxFee: yup.number().min(0).required(),
            bizumTrxFlatFee: yup.number().min(0).required(),
            bizumTrxFee: yup.number().min(0).required()
          })
        )
      })
      .nullable(),
    extra: yup
      .array()
      .of(
        yup.object().shape({
          highRiskFee: yup.number().min(0),
          dailyPlatformFee: yup.number().min(0),
          dailyExtraSeatsFee: yup.number().min(0),
          amount: yup.number().min(0).required(),
          name: yup.string().required(),
          type: yup.string().required(),
          region: yup.string().when('type', {
            is: AccountExtraFeeType.PERCENT,
            then: yup.string().required()
          }),
          masterAccountFee: yup.string().nullable(),
          paymentMethods: yup.array().of(yup.string()).nullable()
        })
      )
      .nullable()
  })
});

export const BillingDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  onSubmit
}) => {
  const {isAdmin} = useAccessLevel();
  const [isEditing, setEditing] = useState(false);

  const {data, loading, refetch} = useAccountActualFeesQuery({
    variables: {id: account?.id!},
    skip: !account
  });

  const fees = data?.accountActualFees as ExtendedAccountFees;

  if (!account || !fees || loading) {
    return <Card {...cardProps} loading title="Billing settings" />;
  }

  const accountFees: ExtendedAccountFees = omitEmpty(account.fees || {}) as ExtendedAccountFees;

  const initialValues: BillingFormValues = {
    id: account.id,
    dynamicBilling: account.dynamicBilling || false,
    billingPlan: account.billingPlan || BillingPlans.MONEI_X,
    customizePlan:
      accountFees?.dailyPlatformFee !== undefined ||
      accountFees?.dailyExtraSeatsFee !== undefined ||
      accountFees?.weeklyCustomDomainFee !== undefined,
    customizeSteps: Boolean(accountFees?.steps),
    fees: {
      ...accountFees,
      ...fees
    },
    iban: account.iban,
    billingVatPercent: account.billingVatPercent ?? accountInvoicesVatOptions.DEFAULT,
    notifications: {
      ...account.notifications,
      emailOnAccountInvoice: account.notifications?.emailOnAccountInvoice ?? false
    },
    billingEmail: account.billingEmail,
    accountInvoicePaymentMethod:
      account.accountInvoicePaymentMethod || AccountInvoicePaymentMethod.DIRECT_DEBIT,
    invoiceNotesString: account.customAccountInvoiceNotes?.join('\n') || '',
    groupInvoicesByStore: account.groupInvoicesByStore || false
  };

  const handleSubmit = async ({
    fees,
    id,
    dynamicBilling,
    billingPlan,
    customizePlan,
    customizeSteps,
    billingVatPercent,
    iban,
    billingEmail,
    notifications,
    accountInvoicePaymentMethod,
    invoiceNotesString,
    groupInvoicesByStore
  }: BillingFormValues) => {
    const feesUpdate = {...fees} as ExtendedAccountFees;
    if (!customizePlan) {
      feesUpdate.dailyExtraSeatsFee = null;
      feesUpdate.dailyPlatformFee = null;
      feesUpdate.weeklyCustomDomainFee = null;
    }
    if (!customizeSteps) {
      feesUpdate.steps = null;
    }
    feesUpdate.extra = omitEmpty(
      feesUpdate.extra?.map((extra) => ({
        ...extra,
        // @ts-ignore
        region: checkIfRegional(extra.region) ? AccountExtraFeeRegion.ALL : extra.region
      })) || []
    );
    if (!feesUpdate.highRiskFee) {
      feesUpdate.highRiskFee = null;
    }

    await onSubmit(
      omitEmpty({
        id,
        billingPlan,
        dynamicBilling,
        fees: feesUpdate,
        billingVatPercent,
        iban,
        billingEmail,
        notifications,
        accountInvoicePaymentMethod,
        customAccountInvoiceNotes: invoiceNotesString.split('\n').filter(Boolean),
        groupInvoicesByStore
      })
    );
    await refetch();
    setEditing(false);
  };

  const columns: Array<ColumnProps<AccountSteps>> = [
    {
      title: 'Starting from',
      dataIndex: 'from',
      align: 'right',
      render: formatBigNumber
    },
    {
      title: 'Card transaction fee',
      dataIndex: 'trxFee',
      render: formatFee
    },
    {
      title: 'Bizum transaction fee',
      dataIndex: 'bizumTrxFee',
      render: formatFee
    },
    {
      title: 'Flat fee per card transaction',
      dataIndex: 'trxFlatFee',
      render: formatFlatFee
    },
    {
      title: 'Flat fee per bizum transaction',
      dataIndex: 'bizumTrxFlatFee',
      render: formatFlatFee
    }
  ];

  return (
    <TableCard
      {...cardProps}
      title="Billing settings"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}>
          <Form {...formProps} style={{padding: 12}}>
            <BillingDetailsForm account={account} formProps={formProps} />
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton disabled={false}>Submit</SubmitButton>
              </Stack>
              <ScrollToError />
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <div>
          <div style={{padding: 12, borderBottom: '1px solid #e8e8e8'}}>
            <PropertyList
              omitEmpty
              labelWidth={280}
              properties={[
                ...(account.dynamicBilling
                  ? [
                      {
                        label: 'Dynamic billing',
                        value: formatBoolean(account.dynamicBilling)
                      },
                      {
                        label: 'Billing plan',
                        value: (account.billingPlan || BillingPlans.MONEI_X).replace('_', ' ')
                      },
                      {
                        label: 'Billing IBAN',
                        value: formatIBAN(account.iban, true)
                      },
                      {
                        label: 'Email admin on new invoice',
                        value: formatBoolean(account.notifications?.emailOnAccountInvoice)
                      },
                      {
                        label: 'Billing email',
                        value: isUndefined(account.billingEmail)
                          ? account.adminEmail
                          : account.billingEmail
                      },
                      {
                        label: 'Payment method',
                        value: formatInvoicePaymentMethod(account.accountInvoicePaymentMethod)
                      },
                      {
                        label: (
                          <Hint
                            message={
                              'These notes will be added to the notes section of the PDF invoice'
                            }
                            title="Custom invoice notes"
                          />
                        ),
                        value:
                          account.customAccountInvoiceNotes?.length &&
                          account.customAccountInvoiceNotes[0] !== '' ? (
                            <CustomAccountInvoiceNotes
                              notes={account.customAccountInvoiceNotes as string[]}
                            />
                          ) : null
                      },
                      {
                        label: (
                          <Hint
                            message="If enabled, invoice line items will be grouped by store"
                            title="Group invoice info by store"
                          />
                        ),
                        value: formatBoolean(account.groupInvoicesByStore)
                      },
                      {
                        label: 'VAT percent',
                        value: formatPercent(
                          account.billingVatPercent ?? accountInvoicesVatOptions.DEFAULT
                        )
                      },
                      {
                        label: 'Daily platform usage fee',
                        value: formatFlatFee(fees?.dailyPlatformFee)
                      },
                      {
                        label: 'High-risk transaction surcharge',
                        value: formatFee(fees?.highRiskFee)
                      },
                      {
                        label: 'Daily extra seat fee',
                        value: formatFlatFee(fees?.dailyExtraSeatsFee)
                      },
                      {
                        label: 'Weekly custom domain fee',
                        value: formatFlatFee(fees?.weeklyCustomDomainFee)
                      },
                      ...(fees?.extra || [])
                        .map((fee) => ({
                          label: fee!.name!,
                          value: formatExtraFee(fee)
                        }))
                        .sort((a, b) => (a.label < b.label ? -1 : 1))
                    ]
                  : [
                      {
                        label: 'Dynamic billing',
                        value: formatBoolean(account.dynamicBilling)
                      }
                    ])
              ]}
            />
          </div>
          {account.dynamicBilling ? (
            <Table dataSource={fees.steps!} columns={columns} rowKey="from" pagination={false} />
          ) : (
            <Alert
              style={{margin: 12}}
              message="This account is not included in the dynamic billing. We will not generate the invoices for it."
              type="error"
            />
          )}
        </div>
      )}
    </TableCard>
  );
};
